/**
 * StoryBlock — heimplanet-inspired image+text storytelling row.
 *
 * Variants:
 *  - alternating image/text rows (default)
 *  - full-bleed hero with overlay text (variant="hero")
 *  - image-only band (variant="band")
 *
 * Designed to be dropped into any page section to add narrative imagery
 * without breaking existing layout.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Tiny in-view hook — IntersectionObserver, zero deps.
function useInView({ threshold = 0.18 } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || inView) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, threshold]);
  return [ref, inView];
}

export function StoryRow({
  image,
  alt = "",
  eyebrow,
  title,
  body,
  caption,
  reverse = false,
  cta,
  testId,
}) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      data-testid={testId}
      className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center reveal ${inView ? "reveal-in" : ""} ${
        reverse ? "md:[&>div:first-child]:order-2" : ""
      }`}
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-secondary/40">
        <img
          src={image}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover img-hover transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent pointer-events-none" />
        {caption && (
          <p className="absolute bottom-4 left-4 right-4 font-mono text-[10px] uppercase tracking-[0.24em] text-white/90 drop-shadow-lg">
            {caption}
          </p>
        )}
      </div>
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h3 className="mt-5 text-h2 leading-[1.05]">
          {title}
        </h3>
        {body && (
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
            {body}
          </p>
        )}
        {cta && (
          <Link
            to={cta.to}
            className="mt-7 inline-flex items-center gap-2 text-sm text-signal-lime hover:gap-3 transition-all"
            data-testid={cta.testId}
          >
            {cta.label} <ArrowRight size={14} />
          </Link>
        )}
      </div>
    </div>
  );
}

export function StoryHero({
  image,
  alt = "",
  eyebrow,
  title,
  body,
  height = "h-[60vh] md:h-[72vh]",
  testId,
  children,
}) {
  return (
    <section
      data-testid={testId}
      className={`relative ${height} overflow-hidden`}
    >
      <img
        src={image}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/30 to-background" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.45)_100%)]" />
      <div className="container-x relative z-10 h-full flex flex-col justify-end pb-12 md:pb-20">
        {eyebrow && (
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-signal-lime">
            {eyebrow}
          </p>
        )}
        {title && (
          <h2 className="mt-4 text-hero font-display tracking-tight leading-[0.95] max-w-4xl text-foreground">
            {title}
          </h2>
        )}
        {body && (
          <p className="mt-5 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
            {body}
          </p>
        )}
        {children}
      </div>
    </section>
  );
}

export function StoryBand({ image, alt = "", caption, overlay = null, height = "h-[45vh] md:h-[55vh]" }) {
  return (
    <section className={`relative ${height} overflow-hidden`}>
      <img
        src={image}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {overlay && (
        <div className="storyband-overlay" aria-hidden>
          <img src={overlay.src} alt="" className="storyband-overlay-img"
               style={{
                 left: overlay.left ?? "39%",
                 top: overlay.top ?? "62%",
                 width: overlay.width ?? "14%",
                 height: overlay.height ?? "5%",
               }} />
        </div>
      )}
      {caption && (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
          <div className="container-x relative z-10 h-full flex items-end pb-10">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-white/90 max-w-xl drop-shadow-lg">
              {caption}
            </p>
          </div>
        </>
      )}
    </section>
  );
}

export default StoryRow;
